// ABOUTME: Editable preview of the purchase order mail to a supplier: recipient, subject field and the rendered mail.
// ABOUTME: The mail is edited in place (designMode iframe, as in EmailTemplateEditor); every edit reports { subject, html }.

import { Box, useTheme } from "@mui/material";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import TextInput from "../../theme/inputs/TextInput";
import Text from "../../theme/text/Text";
import { tokens } from "../../../theme";

// mail: { to, subject, html } as the backend built it; html is only read when the frame mounts,
// so a parent remounts this component (key) to show a freshly built mail.
const SupplierMailEditor = ({ mail, subject, onChange }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const iframeRef = useRef(null);
    const subjectRef = useRef(subject);
    subjectRef.current = subject;

    const readHtml = useCallback(() => {
        const doc = iframeRef.current?.contentDocument;
        if (doc && doc.body && doc.body.innerHTML.trim() !== "") {
            return "<!DOCTYPE html>" + doc.documentElement.outerHTML;
        }
        return mail.html;
    }, [mail.html]);

    // Sandbox (same-origin, no scripts) keeps template content from running code inside the app.
    const handleIframeLoad = useCallback((event) => {
        try {
            const doc = event.target.contentDocument;
            doc.designMode = "on";
            doc.addEventListener("input", () => onChange({ subject: subjectRef.current, html: readHtml() }));
        } catch (e) {
            // Inaccessible document — the frame stays read-only.
        }
    }, [onChange, readHtml]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 120 }}><Text light>{t("mail_recipient")}</Text></Box>
                <Text>{mail.to}</Text>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 120 }}><Text light>{t("email_subject")}</Text></Box>
                <Box sx={{ flex: 1 }}>
                    <TextInput
                        value={subject}
                        onChange={(e) => onChange({ subject: e.target.value, html: readHtml() })}
                    />
                </Box>
            </Box>
            <Box
                component="iframe"
                ref={iframeRef}
                title={t("mail_preview")}
                sandbox="allow-same-origin"
                srcDoc={mail.html}
                onLoad={handleIframeLoad}
                data-testid="supplier-mail-frame"
                sx={{
                    width: 1,
                    height: 360,
                    border: "none",
                    borderRadius: 2,
                    background: colors.grey[100],
                }}
            />
        </Box>
    );
};

export default SupplierMailEditor;
